<template>
  <ModalComponent
    ref="modalRef"
    size="lg"
    :submitButtonText="init.isNew ? 'Add consumption location' : 'Save changes'"
    :submit="save"
    @close="$emit('close')"
  >
    <template #title>
      <span class="cg-micro d-block">{{ init.companyName }}</span>
      {{
        init.isNew ? "Add consumption location" : "Edit consumption location"
      }}
    </template>
    <template #body>
      <div class="d-flex flex-column gap-3">
        <div>
          <label class="cg-micro mb-1" :for="`${uid}-name`">Name</label>
          <input
            :id="`${uid}-name`"
            v-model="form.name"
            type="text"
            class="form-control form-control-sm"
            placeholder="Paris DC1"
          />
        </div>
        <div>
          <label class="cg-micro mb-1" :for="`${uid}-id`"
            >Consumption location identifier
            <span class="text-body-secondary text-lowercase fw-normal"
              >optional</span
            ></label
          >
          <input
            :id="`${uid}-id`"
            v-model="form.mpid"
            type="text"
            class="form-control form-control-sm cg-mono"
            placeholder="e.g. PRM 14827364920184"
          />
          <p class="mb-0 mt-1 text-body-secondary small">
            Leave empty if you don't have one — for example a sublet space where
            electricity is billed through your landlord.
          </p>
        </div>
        <div>
          <label class="cg-micro mb-1" :for="`${uid}-supplier`">
            Electricity supplier
            <span
              v-if="init.supplierRequired"
              class="text-warning-emphasis text-lowercase fw-normal"
              >required</span
            >
            <span v-else class="text-body-secondary text-lowercase fw-normal"
              >optional</span
            >
          </label>
          <input
            :id="`${uid}-supplier`"
            v-model="form.supplier"
            type="text"
            class="form-control form-control-sm"
            :class="{ 'is-invalid': supplierError }"
            placeholder="Supplier or landlord on the invoice"
          />
          <p class="mb-0 mt-1 text-body-secondary small">
            {{
              init.supplierRequired
                ? `${init.countryName} cancellations are executed by the electricity supplier, so every consumption location needs one on file.`
                : `Soldera cancels directly in ${init.countryName}, so this is only for your own records. Name the landlord if the electricity is billed through them.`
            }}
          </p>
          <p v-if="supplierError" class="mb-0 mt-1 text-warning-emphasis small">
            Name the supplier before saving this consumption location.
          </p>
        </div>
        <div>
          <div
            class="d-flex justify-content-between align-items-baseline gap-3 mb-2"
          >
            <p class="cg-micro mb-0">Expected 2026 consumption</p>
            <StatBlock :value="fmt(total)" unit="MWh" />
          </div>
          <div class="d-flex align-items-center gap-2 mb-3">
            <input
              v-model="annual"
              type="text"
              inputmode="numeric"
              class="form-control form-control-sm text-end cg-tabular cg-annual"
              :class="{ 'is-invalid': annualError }"
              placeholder="Yearly figure"
              @input="spread"
            />
            <span class="text-body-secondary small"
              >Enter a yearly figure to spread it evenly, then adjust any period
              you know better.</span
            >
          </div>
          <div
            class="d-flex gap-1 align-items-end border rounded p-2 bg-body-secondary"
          >
            <div
              v-for="(p, k) in periodCells"
              :key="p.label"
              class="flex-grow-1 d-flex flex-column gap-1 min-w-0"
            >
              <div class="cg-mini-track d-flex align-items-end">
                <div class="w-100 cg-mini-bar" :style="{ height: p.height }" />
              </div>
              <input
                type="text"
                inputmode="numeric"
                class="form-control form-control-sm text-center cg-tabular cg-period-input"
                :class="{ 'is-invalid': invalidPeriods.has(k) }"
                :value="periodDrafts[k] ?? p.value"
                placeholder="0"
                @change="
                  setPeriod(k, ($event.target as HTMLInputElement).value)
                "
              />
              <span class="cg-micro text-center">{{ p.label }}</span>
            </div>
          </div>
          <p
            v-if="hasVolumeError"
            class="mb-0 mt-2 text-warning-emphasis small"
          >
            Enter a whole or decimal MWh figure without a minus sign.
          </p>
          <p class="mb-0 mt-2 text-body-secondary small">
            Rough estimates are fine. Reported meter readings and approved
            invoices replace them as each period is reported.
          </p>
        </div>
        <div v-if="!init.isNew">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="$emit('remove', { id: init.id, added: init.added })"
          >
            Remove
          </button>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import type { Cadence } from "@/compliance_groups/fixture/complianceGroupsTypes";
import { periods, STEP } from "@/compliance_groups/fixture/derive";
import { fmt, parseVolume, sum } from "@/compliance_groups/fixture/format";
import type {
  LocationFormSubmit,
  LocationRef,
} from "@/compliance_groups/useComplianceDemoStore";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { computed, onMounted, reactive, ref, useId, useTemplateRef } from "vue";
import StatBlock from "./StatBlock.vue";

export interface LocationFormInit {
  isNew: boolean;
  added: boolean;
  id: string;
  companyName: string;
  countryName: string;
  cadence: Cadence;
  supplierRequired: boolean;
  name: string;
  mpid: string;
  supplier: string;
  months: number[];
}

const props = defineProps<{ init: LocationFormInit }>();
const emit = defineEmits<{
  save: [submit: LocationFormSubmit];
  remove: [ref: LocationRef];
  close: [];
}>();

const uid = useId();
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
onMounted(() => modalRef.value?.toggleModal());

const form = reactive({
  name: props.init.name,
  mpid: props.init.mpid,
  supplier: props.init.supplier,
  months: [...props.init.months],
});
const annual = ref("");
const supplierError = ref(false);
const annualError = ref(false);
// Per-period validation: an invalid draft keeps its raw text on screen and blocks saving until
// it is corrected, so one valid period can never clear another period's error.
const periodDrafts = reactive<Record<number, string>>({});
const invalidPeriods = reactive(new Set<number>());
const hasVolumeError = computed(
  () => annualError.value || invalidPeriods.size > 0
);

// One input per reporting period (the grain the country reports on); values spread evenly across the
// months behind each period so the twelve-month record stays consistent.
const periodDefs = periods(STEP[props.init.cadence]);
const total = computed(() => sum(form.months));
const periodCells = computed(() => {
  const values = periodDefs.map((p) =>
    sum(p.months.map((i) => form.months[i] ?? 0))
  );
  const peak = Math.max(1, ...values);
  return periodDefs.map((p, k) => ({
    label: p.label.replace(" 2026", ""),
    value: values[k] ? String(values[k]) : "",
    height: `${Math.max(values[k] ? 3 : 0, Math.round((values[k] / peak) * 44))}px`,
  }));
});
// Even shares at two-decimal precision; the last month absorbs the rounding remainder so the
// periods always add back up to the figure that was typed.
const round2 = (n: number) => Math.round(n * 100) / 100;
const spreadAcross = (indexes: number[], amount: number) => {
  const per = round2(amount / indexes.length);
  indexes.forEach(
    (i, j) =>
      (form.months[i] =
        j === indexes.length - 1
          ? round2(amount - per * (indexes.length - 1))
          : per)
  );
};
// Invalid or negative figures are rejected at the input, never sanitised into a wrong number.
const spread = () => {
  const value = parseVolume(annual.value);
  annualError.value = value === null;
  // A cleared yearly field is a real 0 and zeroes the months; only invalid text is ignored.
  if (value !== null)
    spreadAcross(
      form.months.map((_, i) => i),
      value
    );
};
const setPeriod = (k: number, raw: string) => {
  const value = parseVolume(raw);
  if (value === null) {
    periodDrafts[k] = raw;
    invalidPeriods.add(k);
    return;
  }
  delete periodDrafts[k];
  invalidPeriods.delete(k);
  spreadAcross(periodDefs[k].months, value);
};

const save = () => {
  if (props.init.supplierRequired && !form.supplier.trim()) {
    supplierError.value = true;
    return;
  }
  if (hasVolumeError.value) return;
  emit("save", {
    isNew: props.init.isNew,
    added: props.init.added,
    id: props.init.id,
    companyName: props.init.companyName,
    record: {
      name: form.name,
      mpid: form.mpid,
      supplier: form.supplier,
      months: form.months.map((v) => v || 0),
    },
  });
  modalRef.value?.toggleModal();
};
</script>

<style scoped lang="scss">
.cg-annual {
  width: 132px;
}
.cg-mini-track {
  height: 44px;
}
.cg-mini-bar {
  background: var(--phoenix-primary);
  border-radius: 2px 2px 0 0;
}
.cg-period-input {
  font-size: 0.75rem;
  padding: 4px 2px;
  min-width: 0;
}
</style>
